import { SmartToy } from '@mui/icons-material';
import {
    Avatar,
    alpha,
    Box,
    Drawer,
    IconButton,
    Menu,
    MenuItem,
    Popover,
    Typography,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import { X as Close, Ellipsis, Fullscreen, History, Menu as MenuIcon, MessageSquarePlus } from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { useChatAgent } from '../../Agents/hooks/useChatAgent';
import { MvpAgentChatDialog } from '../ChatAgentDialog/MvpAgentChatDialog';
import AgentActionPanel from './components/AgentActionPanel';
import ChatHistoryPanel from './components/ChatHistoryPanel';
import ChatPanel from './components/ChatPanel';
import { renderFormattedText } from './components/TextFormatter';
import { agentActions, chatThreads, highlightedShortcut, shortcuts } from './components/types';
import MvpChatCard from './MvpChatCard';

const uiTexts = {
    chatTitle: 'T4LO Assistent',
    chatSubtitle: 'Ihr persönlicher KI-Assistent',
};

interface EnhancedMvpChatAgentProps {
    fullWidth?: boolean;
}

export const EnhancedMvpChatAgent: React.FC<EnhancedMvpChatAgentProps> = ({ fullWidth = false }) => {
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).chatAgent;
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    // Debug: Log isMobile state
    console.log('EnhancedMvpChatAgent - isMobile:', isMobile);

    const {
        chat,
        messages,
        isTyping,
        sendMessage: sendChatMessage,
        resetChat,
        getChatList,
        loadChat,
    } = useChatAgent({
        dummyMessage: langStrings.howCanIHelp || 'Hallo! Wie kann ich Ihnen heute helfen?',
    });
    const chatList = useSelector((state: AppState) => state.chatAgent.chatList);
    const [input, setInput] = useState('');
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [historyExpanded, setHistoryExpanded] = useState(false);
    const [actionsExpanded, setActionsExpanded] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [fullscreenDialogOpen, setFullscreenDialogOpen] = useState(false);
    const [historyPopoverOpen, setHistoryPopoverOpen] = useState(false);
    const [historyAnchorEl, setHistoryAnchorEl] = useState<null | HTMLElement>(null);

    const scrollRef = useRef<HTMLDivElement | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);

    // Handle shortcut action
    const handleShortcutAction = (action: string) => {
        setInput(action);
        if (inputRef.current) {
            inputRef.current.focus();
        }
    };

    // AGENT IN ACTION - Flagging System: Aktiviere diese Logik für Agent-Aktionen TODO:
    const [isAgentAction, setIsAgentAction] = useState(false);
    const ENABLE_AGENT_ACTION_PANEL = false; // Feature Flag: Setze auf true um AgentActionPanel zu aktivieren

    // AGENT IN ACTION - Handle action selection: Auskommentiert für zukünftige Verwendung
    const handleActionSelect = (text: string) => {
        if (ENABLE_AGENT_ACTION_PANEL) {
            setInput(text);
            setIsAgentAction(true);
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }
    };

    // Menu handling
    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    // Toggle functions for expandable panels
    const toggleHistoryExpanded = () => {
        setHistoryExpanded(!historyExpanded);
        if (!historyExpanded) {
            setActionsExpanded(false);
            getChatList();
        }
    };

    // History popover handlers
    const handleHistoryClick = (event: React.MouseEvent<HTMLElement>) => {
        setHistoryAnchorEl(event.currentTarget);
        setHistoryPopoverOpen(true);
        getChatList();
    };

    const handleHistoryClose = () => {
        setHistoryPopoverOpen(false);
        setHistoryAnchorEl(null);
    };

    const toggleActionsExpanded = () => {
        setActionsExpanded(!actionsExpanded);
        if (!actionsExpanded) {
            setHistoryExpanded(false);
        }
    };

    const clearChat = () => {
        resetChat();
        handleMenuClose();
    };

    const handleNewChat = () => {
        resetChat();
        setInput('');
    };

    const sendMessage = async () => {
        const trimmedInput = input.trim();
        if (!trimmedInput) return;

        const success = await sendChatMessage(trimmedInput);
        if (success) {
            setInput('');
            setIsAgentAction(false); // Zurücksetzen des Flags nach dem Senden
        }
    };

    const handleKeyPress = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage();
        }
    };

    // Scroll to bottom when messages change and focus input after typing ends
    useEffect(() => {
        if (scrollRef.current) {
            scrollRef.current.scrollTo({
                top: scrollRef.current.scrollHeight,
                behavior: 'smooth',
            });
        }

        // Scroll input into view when typing stops (but don't focus)
        if (!isTyping && inputRef.current && messages.length > 0) {
            setTimeout(() => {
                inputRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }, 500); // Small delay to ensure message rendering is complete
        }
    }, [messages, isTyping]);

    // Menu button for card header
    const menuButton = (
        <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton
                color="inherit"
                onClick={handleNewChat}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title={langStrings.newChat}
                aria-label={langStrings.newChat}
            >
                <MessageSquarePlus size={isMobile ? 16 : 20} />
            </IconButton>
            <IconButton
                color="inherit"
                onClick={handleHistoryClick}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title="Chat History"
                aria-label="Chat History"
            >
                <History size={isMobile ? 16 : 20} />
            </IconButton>
            <IconButton
                color="inherit"
                onClick={() => {
                    console.log('Fullscreen button clicked (desktop)');
                    setFullscreenDialogOpen(true);
                }}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title="Fullscreen"
            >
                <Fullscreen size={isMobile ? 16 : 20} />
            </IconButton>
            <IconButton
                color="inherit"
                onClick={handleMenuOpen}
                size={isMobile ? 'small' : 'medium'}
                sx={{ color: theme.palette.primary.main }}
                title="Menu"
            >
                <Ellipsis size={isMobile ? 16 : 20} />
            </IconButton>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
            >
                <MenuItem onClick={clearChat}>{langStrings.deleteChat}</MenuItem>
            </Menu>
        </Box>
    );

    return (
        <MvpChatCard
            highlightLeftBar={false}
            title={
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar
                            sx={{
                                bgcolor: theme.palette.primary.main,
                                height: isMobile ? '3rem' : '3rem',
                                width: isMobile ? '3rem' : '3rem',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: `0 0 0.75rem ${alpha(theme.palette.primary.main, 0.7)}`,
                                    transform: 'scale(1.05)',
                                },
                            }}
                        >
                            <SmartToy
                                sx={{
                                    fontSize: isMobile ? '1.5rem' : '2rem',
                                    color: theme.palette.primary.contrastText,
                                }}
                            />
                        </Avatar>
                    </Box>
                    <Box>
                        <Typography
                            variant="h3"
                            sx={{
                                fontWeight: 600,
                                lineHeight: 1,
                                textAlign: 'left',
                                ml: 2,
                                color: theme.palette.text.primary,
                                fontFamily: '"Bricolage Grotesque", sans-serif',
                                letterSpacing: '-0.05em',
                            }}
                        >
                            {uiTexts.chatTitle}
                        </Typography>
                        {!isMobile && (
                            <Typography
                                variant="subtitle1"
                                sx={{
                                    fontWeight: 300,
                                    ml: 2,
                                    letterSpacing: '-0.02em',
                                    lineHeight: 1.5,
                                    textAlign: 'left',
                                    color: 'text.secondary',
                                }}
                            >
                                {uiTexts.chatSubtitle}
                            </Typography>
                        )}
                    </Box>
                </Box>
            }
            rightButtons={
                isMobile ? (
                    <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <IconButton
                            color="inherit"
                            onClick={handleHistoryClick}
                            size="small"
                            sx={{ color: theme.palette.primary.main }}
                            title="Chat History"
                        >
                            <History size={16} />
                        </IconButton>
                        <IconButton
                            color="inherit"
                            onClick={() => {
                                console.log('Fullscreen button clicked');
                                setFullscreenDialogOpen(true);
                            }}
                            size="small"
                            sx={{ color: theme.palette.primary.main }}
                            title="Fullscreen"
                        >
                            <Fullscreen size={16} />
                        </IconButton>
                        <IconButton
                            color="inherit"
                            onClick={() => setDrawerOpen(true)}
                            size="small"
                            sx={{ color: theme.palette.primary.main }}
                            title="Menu"
                        >
                            <MenuIcon size={16} />
                        </IconButton>
                    </Box>
                ) : (
                    menuButton
                )
            }
            style={{
                height: 'auto',
                minHeight: isMobile ? 'auto' : '300px',
                maxHeight: 'none',
                width: fullWidth ? '100%' : '800px',
                maxWidth: '100%',
                borderRadius: 1,
                overflow: 'hidden',
                background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.2)} 0%, ${alpha(theme.palette.secondary.main, 0.2)} 100%)`,
                backdropFilter: 'blur(10px)',
                transition: 'all 0.3s ease-in-out',
                padding: isMobile ? '16px' : '24px',
                boxShadow: theme.shadows[4],
            }}
        >
            <Box
                sx={{ p: 0, flexGrow: 1, display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : '100%' }}
            >
                {isMobile ? (
                    /* Mobile Layout with content panels and drawer - optimized for smallest viewport (375x667) */
                    <Box
                        sx={{
                            minHeight: 'auto',
                            height: 'auto',
                            display: 'flex',
                            flexDirection: 'column',
                            overflow: 'hidden',
                            position: 'relative',
                        }}
                    >
                        <Box sx={{ flex: 1, overflow: 'auto', maxHeight: '60vh' }}>
                            <ChatPanel
                                messages={messages}
                                input={input}
                                setInput={setInput}
                                sendMessage={sendMessage}
                                handleKeyPress={handleKeyPress}
                                isTyping={isTyping}
                                chat={chat}
                                scrollRef={scrollRef}
                                inputRef={inputRef}
                                renderFormattedText={renderFormattedText}
                                langStrings={langStrings}
                                shortcuts={shortcuts}
                                highlightedShortcut={highlightedShortcut}
                                handleShortcutAction={handleShortcutAction}
                                isMobile={true}
                                isAgentAction={isAgentAction}
                            />
                        </Box>

                        {/* Drawer for AgentActionPanel only */}
                        <Drawer
                            anchor="right"
                            open={drawerOpen}
                            onClose={() => setDrawerOpen(false)}
                            sx={{
                                '& .MuiDrawer-paper': {
                                    width: '80%',
                                    maxWidth: '300px',
                                    height: '75%',
                                    maxHeight: '600px',
                                    top: '12.5%', // Zentriert vertikal (100% - 75%) / 2 = 12.5%
                                    p: 2,
                                    pt: 0,
                                    borderRadius: '8px 0 0 8px',
                                    bgcolor: alpha(theme.palette.background.paper, 0.9),
                                    backdropFilter: 'blur(10px)',
                                },
                            }}
                            variant="temporary"
                            ModalProps={{
                                keepMounted: true, // Bessere Performance beim Öffnen
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    justifyContent: 'flex-end',
                                    p: 1,
                                }}
                            >
                                <IconButton onClick={() => setDrawerOpen(false)}>
                                    <Close size={20} />
                                </IconButton>
                            </Box>

                            <Box
                                sx={{
                                    width: '100%',
                                    height: 'calc(100% - 40px)', // Volle Höhe minus Header
                                    overflow: 'auto',
                                    display: 'flex',
                                    flexDirection: 'column',
                                }}
                            >
                                {/* AGENT IN ACTION - Mobile: AgentActionPanel auskommentiert für zukünftige Verwendung */}
                                {ENABLE_AGENT_ACTION_PANEL && (
                                    <AgentActionPanel
                                        actions={agentActions}
                                        isExpanded={actionsExpanded}
                                        onToggle={toggleActionsExpanded}
                                        onActionSelect={(text) => {
                                            handleActionSelect(text);
                                            setDrawerOpen(false);
                                        }}
                                    />
                                )}
                            </Box>
                        </Drawer>
                    </Box>
                ) : (
                    /* Desktop Layout with side-by-side panels */
                    <Box sx={{ display: 'flex', height: '100%' }}>
                        {/* Chat panel - full width when history is collapsed */}
                        <Box
                            sx={{
                                flex: 1,
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                            }}
                        >
                            <ChatPanel
                                messages={messages}
                                input={input}
                                setInput={setInput}
                                sendMessage={sendMessage}
                                handleKeyPress={handleKeyPress}
                                isTyping={isTyping}
                                chat={chat}
                                scrollRef={scrollRef}
                                inputRef={inputRef}
                                renderFormattedText={renderFormattedText}
                                langStrings={langStrings}
                                shortcuts={shortcuts}
                                highlightedShortcut={highlightedShortcut}
                                handleShortcutAction={handleShortcutAction}
                                isMobile={false}
                                isAgentAction={isAgentAction}
                            />
                        </Box>
                    </Box>
                )}
            </Box>

            {/* Fullscreen Dialog */}
            <MvpAgentChatDialog open={fullscreenDialogOpen} onClose={() => setFullscreenDialogOpen(false)} />

            {/* History Popover */}
            <Popover
                open={historyPopoverOpen}
                anchorEl={historyAnchorEl}
                onClose={handleHistoryClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'right',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'right',
                }}
                sx={{
                    mt: 2,
                    '& .MuiPopover-paper': {
                        width: isMobile ? '90vw' : '400px',
                        maxWidth: isMobile ? '90vw' : '400px',
                        height: isMobile ? '70vh' : '500px',
                        maxHeight: isMobile ? '70vh' : '500px',
                        bgcolor: alpha(theme.palette.background.paper, 0.95),
                        backdropFilter: 'blur(10px)',
                        border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
                        overflow: 'hidden',
                    },
                }}
            >
                <ChatHistoryPanel
                    chatThreads={chatList.map((chat) => ({
                        id: chat.chatId,
                        title: chat.title,
                        lastMessage: '',
                        timestamp: `${new Date(chat.createdAt).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: '2-digit' })} ${new Date(chat.createdAt).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`,
                        unread: false,
                    }))}
                    historyExpanded={true}
                    onToggleHistory={() => {}}
                    onLoadChat={(chatId) => {
                        loadChat(chatId);
                        handleHistoryClose();
                    }}
                    onRefreshChats={getChatList}
                    currentChatId={chat?.chatId}
                    langStrings={langStrings}
                    onClose={handleHistoryClose}
                />
            </Popover>
        </MvpChatCard>
    );
};

export default EnhancedMvpChatAgent;
